import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const AdminCalendarioEvents = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ day: '', title: '', description: '', event_type: 'custom' });

  useEffect(() => { fetchEvents(); }, [year, month]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/campus/calendario/events/${year}/${month}`);
      setEvents(res.data.events || []);
    } catch { setEvents([]); }
    setLoading(false);
  };

  const addEvent = async () => {
    if (!form.day || !form.title) return toast.error('Completa día y título');
    const dayNum = parseInt(form.day);
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) return toast.error('Día inválido');
    setSaving(true);
    try {
      await api.post(`/admin/campus/calendario/events/${year}/${month}`, { ...form, day: dayNum });
      toast.success('Evento añadido');
      setForm({ day: '', title: '', description: '', event_type: 'custom' });
      setAdding(false);
      fetchEvents();
    } catch { toast.error('Error al añadir'); }
    setSaving(false);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try {
      await api.delete(`/admin/campus/calendario/events/${year}/${month}/${id}`);
      toast.success('Evento eliminado');
      fetchEvents();
    } catch { toast.error('Error'); }
  };

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const goNext = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  return (
    <div className="space-y-4" data-testid="admin-calendario-events">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={goPrev} className="text-[#c9a227]/40 hover:text-[#c9a227]/80 p-1" data-testid="admin-cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-[#5c4a2a] min-w-[140px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button onClick={goNext} className="text-[#c9a227]/40 hover:text-[#c9a227]/80 p-1" data-testid="admin-cal-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <button onClick={() => setAdding(!adding)}
          className="flex items-center gap-1 text-xs text-[#c9a227] hover:text-[#8b6914]">
          <Plus className="w-3.5 h-3.5" /> Añadir evento
        </button>
      </div>

      <p className="text-xs text-[#8b7340]/40">
        Las fases lunares, portales numéricos y sabbats se generan automáticamente. Aquí puedes añadir eventos especiales.
      </p>

      {adding && (
        <div className="p-4 border border-[#c9a227]/20 rounded-lg bg-[#faf6f0] space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Día</label>
              <input type="number" min="1" max="31" value={form.day} onChange={e => setForm({ ...form, day: e.target.value })}
                placeholder="15" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Título</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Círculo de Luna Llena" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Descripción (opcional)</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Meditación colectiva a las 21h" className="w-full mt-1 text-sm border border-[#e8e2d8] rounded px-3 py-1.5 focus:outline-none focus:border-[#c9a227]/40" />
          </div>
          <button onClick={addEvent} disabled={saving}
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#c9a227]/10 text-[#8b6914] hover:bg-[#c9a227]/20 border border-[#c9a227]/20">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Guardar
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <p className="text-center text-sm text-[#8b7340]/30 py-6 italic">No hay eventos personalizados para este mes.</p>
      ) : (
        <div className="space-y-2">
          {events.sort((a, b) => (a.day || 0) - (b.day || 0)).map(ev => (
            <div key={ev.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#e8e2d8] bg-white" data-testid={`cal-event-${ev.id}`}>
              <span className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xs font-medium flex-shrink-0">
                {ev.day}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#5c4a2a]">{ev.title}</p>
                {ev.description && <p className="text-xs text-[#8b7340]/50 truncate">{ev.description}</p>}
              </div>
              <button onClick={() => deleteEvent(ev.id)} className="text-red-300 hover:text-red-500 flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCalendarioEvents;
